import React from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { palette } from "../../styles/theme";
import { resolveSpecsImageUrl } from "./specsAssetUrls";
import { AiTransformModal } from "./SpecsEditorAiTransformModal";
import { logSpecsModelPath } from "./specsModelPathLogger";
import {
  DEFAULT_SPECS_IMAGE_UPLOAD_ACCEPT,
  getSpecsImageExtension,
  type SpecsImageUploadContext,
  uploadSpecsImageFile,
  uploadSpecsImageUrl,
} from "./specsImageUpload";
import { type OutputImageRole } from "./types";

type ImagePickerSource = {
  label: string;
  imageUrl: string;
};

export type SpecsImageEditorProps = {
  label: string;
  value: string | null;
  fileNamePrefix: string;
  uploadContext: SpecsImageUploadContext;
  sourceOptions: ImagePickerSource[];
  role?: OutputImageRole;
  debugPath?: string;
  onChange: (url: string | null) => void;
};

function getUploadFileExtension(file: File) {
  return /\.psd$/i.test(file.name) ? "psd" : getSpecsImageExtension(file.type);
}

export function SpecsImageEditor({
  label,
  value,
  fileNamePrefix,
  uploadContext,
  sourceOptions,
  role = "none",
  debugPath,
  onChange,
}: SpecsImageEditorProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [aiOpen, setAiOpen] = React.useState(false);
  const sourceOptionsWithCurrent = value
    ? [
        { label: "Current image", imageUrl: value },
        ...sourceOptions.filter((source) => source.imageUrl !== value),
      ]
    : sourceOptions;
  const selectedValue = value ?? "";

  const uploadSourceUrl = async (sourceUrl: string) => {
    onChange(
      await uploadSpecsImageUrl(sourceUrl, fileNamePrefix, uploadContext)
    );
  };

  const uploadFile = async (file: File) => {
    const uploadFile = new File(
      [file],
      `${fileNamePrefix}.${getUploadFileExtension(file)}`,
      { type: file.type, lastModified: file.lastModified }
    );
    const uploadedUrl = await uploadSpecsImageFile(uploadFile, uploadContext);
    onChange(uploadedUrl);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        onClick={debugPath ? () => logSpecsModelPath(debugPath) : undefined}
        sx={{
          mb: 0.5,
          display: "block",
          cursor: debugPath ? "pointer" : undefined,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" gap={1} sx={{ mb: 1, minWidth: 0 }}>
        <Select
          displayEmpty
          size="small"
          fullWidth
          value={selectedValue}
          onChange={(event) => {
            const sourceUrl = event.target.value;
            if (sourceUrl) {
              void uploadSourceUrl(sourceUrl);
            }
          }}
          sx={{
            minWidth: 0,
            flex: "1 1 0",
            ".MuiSelect-select": {
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              Select source
            </Typography>
          </MenuItem>
          {sourceOptionsWithCurrent.map((source) => (
            <MenuItem key={source.imageUrl} value={source.imageUrl}>
              {source.label}
            </MenuItem>
          ))}
        </Select>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileUploadOutlinedIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0 }}
        >
          Upload
        </Button>
        <Button
          size="small"
          variant={value ? "outlined" : "contained"}
          startIcon={<AutoFixHighIcon />}
          onClick={() => setAiOpen(true)}
          disabled={!value}
          sx={{ flexShrink: 0 }}
        >
          AI
        </Button>
        {value ? (
          <Tooltip title={`Remove ${label}`}>
            <IconButton
              size="small"
              aria-label={`Remove ${label}`}
              onClick={() => onChange(null)}
              sx={{ flexShrink: 0 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      {value ? (
        <Box
          sx={{
            height: 160,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: palette.grey.lightBackground,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={resolveSpecsImageUrl(value)}
            alt={label}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        accept={DEFAULT_SPECS_IMAGE_UPLOAD_ACCEPT}
        style={{ display: "none" }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) {
            void uploadFile(file);
          }
        }}
      />
      <AiTransformModal
        open={aiOpen}
        label={label}
        role={role}
        sourceImageUrl={value}
        fileNamePrefix={fileNamePrefix}
        uploadContext={uploadContext}
        sourceOptions={sourceOptionsWithCurrent}
        selectedSourceUrl={selectedValue}
        onClose={() => setAiOpen(false)}
        onSelectSource={(sourceUrl) => {
          void uploadSourceUrl(sourceUrl);
        }}
        onChange={onChange}
      />
    </Box>
  );
}
